import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useColorContext } from '../context/colorContext';
import { Button, import_material_button}  from '../components/ui/buttons/Button';
import AudioBlock from '../components/features/audioFeatures/audioBlock';
import { CreateNoteButton, AudioPlayButton } from '../components/ui/buttons/Button';
import MindMap from '../components/features/minMapFeatures/mindMapFeatures';
import { ReduceExpandButton, FullscreenToggleButton, PlusButton, MinusButton }  from '../components/ui/buttons/Button';
import { createRootNode, addNodeToParentById, removeNodeById } from '../components/features/minMapFeatures/mindMapSettingsFunctions';
import { FiUpload } from 'react-icons/fi';

import useAISummary from '../hooks/useAISummary';
import {
  ImportRecordAudioFeature,
  ImportFileFeature,
  PastLinkVideoFeature,
} from '../components/features/buttonsImportFeatures/buttonsImportFeatures';
import MathContentLayer from '../components/ui/math/MathContentLayer';
import CourseEditor from '../components/features/textEditor/textEditor';


const BACKEND_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001';

const ImportButton = import_material_button;

const EMPTY_TIPTAP_DOC = {
  type: 'doc',
  content: [{ type: 'paragraph' }],
};

const isTipTapDoc = (value) => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  if (value.type !== 'doc') return false;
  return Array.isArray(value.content);
};

const tiptapParagraphDocFromText = (value = '') => {
  const text = String(value || '').trim();
  if (!text) return EMPTY_TIPTAP_DOC;
  return {
    type: 'doc',
    content: [
      {
        type: 'paragraph',
        content: [{ type: 'text', text }],
      },
    ],
  };
};

const tiptapPlainText = (node) => {
  if (!node || typeof node !== 'object') return '';
  if (typeof node.text === 'string') return node.text;
  if (!Array.isArray(node.content)) return '';

  const parts = node.content.map((child) => tiptapPlainText(child)).filter(Boolean);
  if (!parts.length) return '';

  if (node.type === 'paragraph' || node.type === 'heading' || node.type === 'listItem') {
    return `${parts.join(' ').trim()}\n`;
  }

  return parts.join(' ');
};

const appendTipTapDoc = (currentContent, nextContent) => {
  const currentText = String(tiptapPlainText(currentContent) || '').trim();
  const nextText = String(tiptapPlainText(nextContent) || '').trim();

  if (!currentText) {
    return nextContent;
  }

  if (!nextText) {
    return currentContent;
  }

  return tiptapParagraphDocFromText(`${currentText}\n\n${nextText}`);
};

const isProbablyYoutubeUrl = (value) => {
  const text = String(value || '').trim().toLowerCase();
  return text.includes('youtube.com') || text.includes('youtu.be');
};

const toSafeHex = (hex, fallback) => {
  const value = String(hex || '').trim();
  return /^#([A-Fa-f0-9]{6})$/.test(value) ? value : fallback;
};

const hexToRgba = (hex, alpha, fallback) => {
  const safeHex = toSafeHex(hex, fallback);
  const r = parseInt(safeHex.slice(1, 3), 16);
  const g = parseInt(safeHex.slice(3, 5), 16);
  const b = parseInt(safeHex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const formatNetworkAwareError = (error, actionLabel) => {
  const message = String(error?.message || '').trim();
  if (/failed to fetch/i.test(message) || /networkerror/i.test(message)) {
    return `${actionLabel}: backend unavailable (${BACKEND_BASE_URL}). Make sure the backend server is running.`;
  }
  return `${actionLabel}: ${message || 'unknown error'}`;
};

const postAudioToTipTap = async (file, options = {}) => {
  const formData = new FormData();
  formData.append('audio', file, file?.name || 'audio');
  if (options?.language) formData.append('language', String(options.language));
  if (options?.transcriptionModel) formData.append('model', String(options.transcriptionModel));
  if (options?.organizeModel) formData.append('organizeModel', String(options.organizeModel));
  if (Number.isFinite(Number(options?.temperature))) formData.append('temperature', String(options.temperature));
  if (Number.isFinite(Number(options?.maxTokens))) formData.append('maxTokens', String(options.maxTokens));
  if (typeof options?.allowFallbackOnApiError === 'boolean') {
    formData.append('allowFallbackOnApiError', String(options.allowFallbackOnApiError));
  }

  let response;
  try {
    response = await fetch(`${BACKEND_BASE_URL}/api/ai/audio/tiptap`, {
      method: 'POST',
      body: formData,
    });
  } catch (error) {
    throw new Error(formatNetworkAwareError(error, 'Echec conversion audio -> TipTap'));
  }

  const payload = await response.json().catch(() => ({}));
  if (!response.ok || !payload?.data) {
    throw new Error(payload?.error || `Echec conversion audio -> TipTap (${response.status})`);
  }

  return payload.data;
};

const postPdfToTipTap = async (file, options = {}) => {
  const formData = new FormData();
  formData.append('pdf', file, file?.name || 'document.pdf');
  const extra = {
    is_ocr: true,
    enable_formula: true,
    enable_table: true,
    model_version: 'vlm',
  };
  if (options?.source_type) extra.source_type = options.source_type;
  formData.append('extraFields', JSON.stringify(extra));

  let response;
  try {
    response = await fetch(`${BACKEND_BASE_URL}/api/ai/pdf/extract`, {
      method: 'POST',
      body: formData,
    });
  } catch (error) {
    throw new Error(formatNetworkAwareError(error, 'Echec conversion PDF -> TipTap'));
  }

  const payload = await response.json().catch(() => ({}));
  if (!response.ok || !payload?.data) {
    throw new Error(payload?.error || `Echec conversion PDF -> TipTap (${response.status})`);
  }

  return payload.data;
};

const postLinkToTipTap = async (link, options = {}) => {
  let response;
  try {
    response = await fetch(`${BACKEND_BASE_URL}/api/ai/link/tiptap`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        url: String(link || '').trim(),
        language: options.language,
        transcriptionModel: options.transcriptionModel,
        organizeModel: options.organizeModel,
        temperature: options.temperature,
        maxTokens: options.maxTokens,
        allowFallbackOnApiError: options.allowFallbackOnApiError,
        webTimeoutMs: options.webTimeoutMs,
        maxChars: options.maxChars,
      }),
    });
  } catch (error) {
    throw new Error(formatNetworkAwareError(error, 'Echec conversion lien -> TipTap'));
  }

  const payload = await response.json().catch(() => ({}));
  if (!response.ok || !payload?.data) {
    throw new Error(payload?.error || `Echec conversion lien -> TipTap (${response.status})`);
  }

  return payload.data;
};


// Initialize with empty root and build structure using utility functions
const buildInitialMindmap = () => {
  let data = createRootNode('My MindMap', 'text');
  
  // Add 4 main categories using the utility function
  data = addNodeToParentById(data, 'root', {
    id: 'visual',
    type: 'text',
    content: 'Visuel',
    isExpanded: true,
    children: [],
  });

  data = addNodeToParentById(data, 'root', {
    id: 'auditif',
    type: 'text',
    content: 'Auditif',
    isExpanded: true,
    children: [],
  });

  data = addNodeToParentById(data, 'root', {
    id: 'lecture',
    type: 'text',
    content: 'Lecture/�0criture',
    isExpanded: true,
    children: [],
  });

  data = addNodeToParentById(data, 'root', {
    id: 'kinesthesique',
    type: 'text',
    content: 'Kinesthetic',
    isExpanded: true,
    children: [],
  });

  return data;
};

const initialMindmapData = buildInitialMindmap();

function TestPage() {
  const navigate = useNavigate();
  const { bgPrimary, bgSecondary, activeAccent, varkColors } = useColorContext();
  const [count, setCount] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [importedContentItems, setImportedContentItems] = useState([]);
  const [youtubeProgress, setYoutubeProgress] = useState(null);
  const [message, setMessage] = useState('');
  const [messages, setMessages] = useState([]);

  const [mindmapData, setMindmapData] = useState(initialMindmapData);
  const [editorContent, setEditorContent] = useState(EMPTY_TIPTAP_DOC);

  const latexExample = "Hello! Here is a formula: $E=mc^2$";
  const safeBgPrimary = toSafeHex(bgPrimary, '#111111');
  const safeBgSecondary = toSafeHex(bgSecondary, '#191919');
  const safeAccent = toSafeHex(activeAccent, '#3B82F6');
  const visualColor = toSafeHex(varkColors?.visual, '#7C3AED');
  const auditoryColor = toSafeHex(varkColors?.auditory, '#3B82F6');

  // OpenAI Test Hook
  const { isLoading, error, result } = useAISummary();
  const renderedNodeCountRef = useRef(0);
  const streamingText = '';
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  const buildMindMapRootFromGeneratedNodes = (nodes) => {
    const generatedNodes = Array.isArray(nodes) ? nodes.filter(Boolean) : [];
    const root = createRootNode('Generating...', 'text');

    if (generatedNodes.length === 0) {
      return root;
    }

    const [chapterNode, ...otherTopLevelNodes] = generatedNodes;
    const chapterChildren = Array.isArray(chapterNode?.children) ? chapterNode.children : [];

    return {
      ...root,
      type: chapterNode?.type || 'text',
      content: chapterNode?.content || 'Cours',
      isExpanded: chapterNode?.isExpanded !== false,
        // Exemple LaTeX pour TextEditor

      buttonAction: chapterNode?.buttonAction,
      children: [...chapterChildren, ...otherTopLevelNodes],
    };
  };

  // Convertir l'image en base64
  const convertImageToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const handleImageSelect = async (e) => {
    const file = e.target.files[0];
    if (file) {
      try {
        const base64 = await convertImageToBase64(file);
        setSelectedImage(base64);
        setImagePreview(URL.createObjectURL(file));
      } catch (error) {
        console.error('Erreur lors de la conversion de l\'image:', error);
      }
    }
  };

  const handleTestOpenAI = async () => {
    const safeEditorDoc = isTipTapDoc(editorContent) ? editorContent : EMPTY_TIPTAP_DOC;

    if (!safeEditorDoc || typeof safeEditorDoc !== 'object') {
      throw new Error('Document TipTap invalide.');
    }

    try {
      console.log('Appel au backend pour generer mindmap...');
      
      // Reset state for a fresh generation
      renderedNodeCountRef.current = 0;
      setMindmapData(createRootNode('Generating...', 'text'));

      // Call backend API with TipTap JSON
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001';
      const response = await fetch(`${backendUrl}/api/ai/mindmap`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          tiptapJson: safeEditorDoc,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }

      const result = await response.json();

      if (!result.success || !Array.isArray(result.nodes)) {
        throw new Error('Reponse invalide du backend.');
      }

      console.log('Nodes generes:', result.nodes.length, 'nodes');
      console.log('Nodes detail:', JSON.stringify(result.nodes, null, 2).substring(0, 500));

      const nextMindMap = buildMindMapRootFromGeneratedNodes(result.nodes);
      console.log('Root cree:', nextMindMap);

      setMindmapData(nextMindMap);
      console.log('setMindmapData complete');
    } catch (err) {
      console.error('Erreur complete:', err);
      console.error('Stack:', err.stack);
      alert(`Erreur: ${err.message}`);
    }
  };

  
  const handleAddVisualChild = () => {
    const newNode = {
      id: `visual-new-${Date.now()}`,
      type: "text",
      content: "Nouveau node visuel",
      isExpanded: true,
      children: [],
    };

    setMindmapData((prev) => addNodeToParentById(prev, "visual", newNode));
  };

  const handleAddLectureChild = () => {
    const newNode = {
      id: `lecture-new-${Date.now()}`,
      type: "button",
      content: "Nouvelle ressource",
      isExpanded: false,
      children: [],
      buttonAction: {
        actionType: "openResource",
        target: "resource-1",
      },
    };

    setMindmapData((prev) => addNodeToParentById(prev, "lecture", newNode));
  };

  const handleRemoveVisualChild = () => {
    // Helper to collect all nodes with visual-new- prefix
    const collectVisualNewNodes = (node, collected = []) => {
      if (node?.id?.startsWith('visual-new-')) {
        collected.push(node.id);
      }
      if (node?.children && Array.isArray(node.children)) {
        node.children.forEach(child => collectVisualNewNodes(child, collected));
      }
      return collected;
    };

    const visualNewIds = collectVisualNewNodes(mindmapData);
    
    if (visualNewIds.length > 0) {
      // Get the last one (most recent)
      const lastVisualNewId = visualNewIds[visualNewIds.length - 1];
      setMindmapData((prev) => removeNodeById(prev, lastVisualNewId));
    }
  };

  const handleAudioImportTest = async (payload) => {
    if (!payload || typeof payload !== 'object') {
      return;
    }

    const sourceFile = payload?.file;
    if (!sourceFile) {
      setImportedContentItems((prev) => [
        ...prev,
        {
          id: `${Date.now()}-audio-${prev.length}`,
          sourceType: 'audio',
          fileName: payload?.fileName || 'Audio sans nom',
          text: payload?.text || 'No valid audio file provided.',
        },
      ]);
      return;
    }

    try {
      const tiptapDoc = await postAudioToTipTap(sourceFile);
      const safeDoc = isTipTapDoc(tiptapDoc) ? tiptapDoc : tiptapParagraphDocFromText('No audio content extracted.');
      const plainText = String(tiptapPlainText(safeDoc) || '').trim() || 'No audio content extracted.';

      setImportedContentItems((prev) => [
        ...prev,
        {
          id: `${Date.now()}-audio-${prev.length}`,
          sourceType: 'audio',
          fileName: payload?.fileName || sourceFile?.name || 'Audio sans nom',
          text: plainText,
        },
      ]);

      setEditorContent((currentContent) => appendTipTapDoc(currentContent, safeDoc));
    } catch (error) {
      setImportedContentItems((prev) => [
        ...prev,
        {
          id: `${Date.now()}-audio-${prev.length}`,
          sourceType: 'audio',
          fileName: payload?.fileName || sourceFile?.name || 'Audio sans nom',
          text: `Extraction audio impossible: ${error?.message || 'erreur inconnue'}`,
        },
      ]);

      setEditorContent(tiptapParagraphDocFromText(`Extraction audio impossible: ${error?.message || 'erreur inconnue'}`));
    }
  };

  const handleFileImportTest = async (payload) => {
    if (!payload || typeof payload !== 'object') return;

    const sourceFile = payload?.file;
    if (!sourceFile) {
      setImportedContentItems((prev) => [
        ...prev,
        {
          id: `${Date.now()}-pdf-${prev.length}`,
          sourceType: 'pdf',
          fileName: payload?.fileName || 'PDF sans nom',
          text: payload?.text || 'No valid PDF file provided.',
        },
      ]);
      return;
    }

    try {
      const tiptapDoc = await postPdfToTipTap(sourceFile, { source_type: payload?.type });
      const safeDoc = isTipTapDoc(tiptapDoc) ? tiptapDoc : tiptapParagraphDocFromText('No text extracted from PDF.');
      const plainText = String(tiptapPlainText(safeDoc) || '').trim() || 'No text extracted from PDF.';

      setImportedContentItems((prev) => [
        ...prev,
        {
          id: `${Date.now()}-pdf-${prev.length}`,
          sourceType: 'pdf',
          fileName: payload?.fileName || sourceFile?.name || 'PDF sans nom',
          text: plainText,
        },
      ]);

      setEditorContent((currentContent) => appendTipTapDoc(currentContent, safeDoc));
    } catch (error) {
      setImportedContentItems((prev) => [
        ...prev,
        {
          id: `${Date.now()}-pdf-${prev.length}`,
          sourceType: 'pdf',
          fileName: payload?.fileName || sourceFile?.name || 'PDF sans nom',
          text: `Extraction PDF impossible: ${error?.message || 'erreur inconnue'}`,
        },
      ]);

      setEditorContent(tiptapParagraphDocFromText(`Extraction PDF impossible: ${error?.message || 'erreur inconnue'}`));
    }
  };

  const handleYoutubeLinkTest = async (link) => {
    const rawLink = typeof link === 'string' ? link.trim() : '';
    const sourceType = isProbablyYoutubeUrl(rawLink) ? 'youtube' : 'web';
    const sourceLabel = sourceType === 'youtube' ? 'YouTube' : 'lien web';
    if (!rawLink) {
      setYoutubeProgress(null);
      setImportedContentItems((prev) => [
        ...prev,
        {
          id: `${Date.now()}-youtube-${prev.length}`,
          sourceType: 'youtube',
          fileName: 'Lien YouTube',
          text: 'Lien YouTube vide.',
        },
      ]);
      return;
    }

    try {
      setYoutubeProgress({
        progress: 0,
        stage: 'start',
        message: 'Initialisation extraction YouTube...',
      });

      setYoutubeProgress((current) => ({
        progress: Math.max(25, Number(current?.progress || 0)),
        stage: 'fetching',
        message: 'Extracting link content...'
      }));

      const tiptapDoc = await postLinkToTipTap(rawLink);
      const safeDoc = isTipTapDoc(tiptapDoc) ? tiptapDoc : tiptapParagraphDocFromText('No content extracted.');
      const extractedText = String(tiptapPlainText(safeDoc) || '').trim() || 'No content extracted.';
      setImportedContentItems((prev) => [
        ...prev,
        {
          id: `${Date.now()}-${sourceType}-${prev.length}`,
          sourceType,
          fileName: sourceType === 'youtube' ? 'Video YouTube' : 'Lien web',
          text: extractedText,
        },
      ]);

      setEditorContent((currentContent) => appendTipTapDoc(currentContent, safeDoc));

      setYoutubeProgress((current) => ({
        progress: 100,
        stage: 'done',
        message: sourceType === 'youtube' ? 'Extraction YouTube terminee.' : 'Extraction web terminee.',
      }));
    } catch (error) {
      const friendlyError = formatNetworkAwareError(error, `Extraction ${sourceLabel} impossible`);
      setYoutubeProgress({
        progress: 100,
        stage: 'error',
        message: `Erreur: ${friendlyError}`,
      });

      setImportedContentItems((prev) => [
        ...prev,
        {
          id: `${Date.now()}-${sourceType}-${prev.length}`,
          sourceType,
          fileName: sourceType === 'youtube' ? 'Lien YouTube' : 'Lien web',
          text: friendlyError,
        },
      ]);
    }
  };

  const handleEditorChange = (nextContent) => {
    if (isTipTapDoc(nextContent)) {
      setEditorContent(nextContent);
      return;
    }

    if (typeof nextContent === 'string' && nextContent.trim()) {
      setEditorContent(tiptapParagraphDocFromText(nextContent));
      return;
    }

    setEditorContent(EMPTY_TIPTAP_DOC);
  };

  return (
    <div style={{
      width: '100%',
      height: '100%',
      backgroundColor: '#000000',
      display: 'flex',
      overflow: 'hidden',
    }}>
      <div className="App" style={{ flex: 1, overflowY: 'auto' }}>
      <div style={{ marginBottom: '20px', display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
        
        <Button 
          variant="primary"
          color="#6D28D9"
          size="md"
          onClick={() => navigate('/')}
        >
          �x�� Back to home
        </Button>
        <button onClick={handleAddVisualChild}>�~" Add to "visual"</button>
        <button onClick={handleAddLectureChild}>�~" Add to "lecture"</button>
        <button onClick={handleRemoveVisualChild} style={{ background: '#dc2626', color: 'white', borderRadius: '4px', border: 'none', padding: '8px 12px', cursor: 'pointer' }}>�R Remove last visual</button>
      </div>

      {/* OpenAI Test Section */}
      <div style={{
        backgroundColor: '#1E1E1E',
        border: '1px solid #2A2A2A',
        borderRadius: '12px',
        padding: '24px',
        marginBottom: '32px',
        maxWidth: '800px',
      }}>
        <h2 style={{
          color: '#FFFFFF',
          fontSize: '24px',
          fontWeight: 'bold',
          marginBottom: '16px',
        }}>
          �x� Test OpenAI API
        </h2>
        
        {/* Image Upload Section */}
        <div style={{
          marginBottom: '16px',
          padding: '16px',
          backgroundColor: '#2A2A2A',
          borderRadius: '8px',
        }}>
          <label style={{
            display: 'block',
            color: '#E0E0E0',
            marginBottom: '12px',
            fontWeight: '600',
          }}>
            �x� Ajouter une image (optionnel)
          </label>
          
          <input
            type="file"
            accept="image/*"
            onChange={handleImageSelect}
            style={{
              display: 'block',
              marginBottom: '12px',
              color: '#E0E0E0',
              backgroundColor: '#1E1E1E',
              border: '1px solid #4A4A4A',
              borderRadius: '4px',
              padding: '8px',
              width: '100%',
              boxSizing: 'border-box',
            }}
          />

          {/* Image Preview */}
          {imagePreview && (
            <div style={{
              marginTop: '12px',
              position: 'relative',
            }}>
              <img 
                src={imagePreview} 
                alt="Preview" 
                style={{
                  maxWidth: '100%',
                  maxHeight: '200px',
                  borderRadius: '8px',
                  border: '2px solid #6D28D9',
                }}
              />
              <button
                onClick={() => {
                  setSelectedImage(null);
                  setImagePreview(null);
                }}
                style={{
                  position: 'absolute',
                  top: '8px',
                  right: '8px',
                  backgroundColor: '#dc2626',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  padding: '4px 8px',
                  cursor: 'pointer',
                  fontSize: '12px',
                  fontWeight: 'bold',
                }}
              >
                �S" Delete
              </button>
            </div>
          )}
        </div>

        <Button 
          variant="primary"
          color="#6D28D9"
          size="lg"
          onClick={handleTestOpenAI}
          disabled={isLoading}
        >
          {isLoading ? '⏳ Chargement...' : selectedImage ? '�xa� Analyser l\'image' : '�xa� Tester OpenAI'}
        </Button>

        {/* Loading State */}
        {isLoading && !streamingText && (
          <div style={{
            marginTop: '16px',
            padding: '12px',
            backgroundColor: '#2A2A2A',
            borderRadius: '8px',
            color: '#E0E0E0',
          }}>
            Generation in progress...
          </div>
        )}

        {/* Streaming last-line indicator */}
        {isLoading && streamingText && (
          <div style={{
            marginTop: '16px',
            padding: '10px 14px',
            backgroundColor: '#111827',
            borderRadius: '8px',
            border: '1px solid #374151',
            fontFamily: 'monospace',
            fontSize: '12px',
            color: '#6B7280',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}>
            <span style={{ color: '#8B5CF6', fontFamily: 'sans-serif', flexShrink: 0 }}>�a�</span>
            {streamingText}
          </div>
        )}

        {/* Error State */}
        {error && (
          <div style={{
            marginTop: '16px',
            padding: '12px',
            backgroundColor: '#3D1F1F',
            border: '1px solid #7D3C3C',
            borderRadius: '8px',
            color: '#FF6B6B',
          }}>
            <strong>�R Erreur:</strong> {error}
          </div>
        )}

        {/* Result State */}
        {result && !isLoading && (
          <div style={{
            marginTop: '16px',
            padding: '16px',
            backgroundColor: '#0F2A1F',
            border: '1px solid #2A5A3F',
            borderRadius: '8px',
            color: '#4ADE80',
            lineHeight: '1.6',
          }}>
            <strong>�S& Response:</strong>
            <div style={{ marginTop: '8px', color: '#E0E0E0' }}>
              {result}
            </div>
          </div>
        )}
      </div>

      <MindMap
        data={mindmapData}
        onDataChange={(newData) => setMindmapData(newData)}
        onButtonAction={(action, nodeId) => console.log(action, nodeId)}
        containerHeight="1250px"
        containerWidth="1250px"
        backgroundColor={safeBgPrimary}
      />
      <CourseEditor
        initialContent={isTipTapDoc(editorContent) ? editorContent : EMPTY_TIPTAP_DOC}
        onChange={handleEditorChange}
        editorBgColor={safeBgSecondary}
        editorTextColor="#E0E0E0"
        toolbarBgColor={safeBgPrimary}
        editorHeight="700px"
        toolbarHeight="20px"
        editorWidth="100%"
        toolbarWidth="60%"
        toolbarStyle={{
          buttonWidth: 60,
          buttonHeight: 60,
          buttonIconSize: 25,
          gap: 10,
        }}
      />
      <header style={{
        background: `linear-gradient(135deg, ${safeBgPrimary} 0%, ${hexToRgba(visualColor, 0.05, safeBgPrimary)} 20%, ${hexToRgba(auditoryColor, 0.05, safeBgPrimary)} 45%, ${hexToRgba(safeAccent, 0.06, safeBgPrimary)} 70%, ${safeBgPrimary} 100%)`,
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'white',
      }}>
        
        
        <div style={{ maxWidth: '900px', padding: '40px', textAlign: 'center' }}>
          
          {/* Title */}
          <h1 style={{ 
            fontSize: '48px', 
            marginBottom: '16px',
            background: 'linear-gradient(135deg, #6D28D9 0%, #8B5CF6 50%, #3B82F6 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
          }}>
            PlyNote Button
          </h1>
          
          <p style={{ color: '#9E9E9E', marginBottom: '48px', fontSize: '18px' }}>
            Customizable colors for any design
          </p>

          {/* Custom Colors - VARK Profiles */}
          <div style={{ marginBottom: '40px' }}>
            <h2 style={{ color: '#E0E0E0', marginBottom: '20px', fontSize: '24px' }}>VARK Profile Colors</h2>
            <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>

<AudioBlock
    audioName="Interactive Activity Guide"
    duration="15:00"
    color="#F59E0B"
    size="md"
    isPlaying={isPlaying}
    onPlayToggle={() => setIsPlaying(!isPlaying)}
/>
<div
  style={{
    position: isFullscreen ? 'fixed' : 'relative',
    inset: isFullscreen ? 0 : 'auto',
    zIndex: isFullscreen ? 9999 : 1,
    width: '100%',
    height: isFullscreen ? '100vh' : '700px',
  }}
>

<FullscreenToggleButton
  isFullscreen={isFullscreen}
  onToggle={() => setIsFullscreen((v) => !v)}
  position="custom"
  top={24}
  right={28}
  borderRadius="14px"
    icon={<FiUpload />}

  backgroundColor="#111827"
  hoverBackgroundColor="#1f2937"
  iconColor="#cbd5e1"
  hoverIconColor="#60a5fa"
  borderColor="rgba(96,165,250,0.25)"
  hoverBorderColor="rgba(96,165,250,0.6)"
  boxShadow="0 2px 10px rgba(0,0,0,0.22)"
  hoverBoxShadow="0 8px 24px rgba(96,165,250,0.3)"
/>
  {/* Mindmap content */}
</div>
<PlusButton
  size="lg"
  color="#4c00ff"
  style={{ position: 'absolute', top: 16, right: 16 }}
  onClick={() => setCount((c) => c + 1)}
/>

<MinusButton
  size="lg"
  color="#4c00ff"
  style={{ position: 'absolute', top: 16, right: 68 }}
  onClick={() => setCount((c) => c - 1)}
/>
<AudioPlayButton 
  size="md"
  color="#2851d9"  // Visual
  isPlaying={isPlaying}
  onClick={() => setIsPlaying(!isPlaying)}
/>


              <Button variant="primary" color="#6D28D9">Visual</Button>
              <Button variant="primary" color="#3B82F6">Auditory</Button>
              <Button variant="primary" color="#059669">Reading</Button>
              <Button variant="primary" color="#F59E0B">Kinesthetic</Button>
            </div>
          </div>

          


          {/* Outline with Custom Colors */}
          <div style={{ marginBottom: '40px' }}>
            <h2 style={{ color: '#E0E0E0', marginBottom: '20px', fontSize: '24px' }}>Outline with Colors</h2>
            <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button variant="outline" color="#6D28D9">Visual</Button>
              <Button variant="outline" color="#3B82F6">Auditory</Button>
              <Button variant="outline" color="#059669">Reading</Button>
              <Button variant="outline" color="#F59E0B">Kinesthetic</Button>
            </div>
          </div>

          {/* More Color Examples */}
          <div style={{ marginBottom: '40px' }}>
            <h2 style={{ color: '#E0E0E0', marginBottom: '20px', fontSize: '24px' }}>More Colors</h2>
            <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button variant="primary" color="#EF4444">Red</Button>
              <Button variant="primary" color="#10B981">Green</Button>
              <Button variant="primary" color="#F59E0B">Orange</Button>
              <Button variant="primary" color="#EC4899">Pink</Button>
              <Button variant="primary" color="#14B8A6">Teal</Button>
            </div>
          </div>

          {/* CreateNoteButton Variants */}
          <div style={{ marginBottom: '40px' }}>
            <h2 style={{ color: '#E0E0E0', marginBottom: '20px', fontSize: '24px' }}>CreateNoteButton Variants</h2>
            <div style={{ marginBottom: '24px' }}>
              <h3 style={{ color: '#B0B0B0', marginBottom: '12px', fontSize: '16px' }}>With Button Background</h3>
              <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
                <CreateNoteButton 
                  size="sm" 
                  color="#6D28D9"
                  onClick={() => alert('Create Visual note')}
                />
                <CreateNoteButton 
                  size="md" 
                  color="#3B82F6"
                  onClick={() => alert('Create Auditory note')}
                />
                <CreateNoteButton 
                  size="lg" 
                  color="#059669"
                  onClick={() => alert('Create Reading note')}
                />
              </div>
            </div>
            <div>
              <h3 style={{ color: '#B0B0B0', marginBottom: '12px', fontSize: '16px' }}>Icon Only (No Background)</h3>
              <div style={{ display: 'flex', gap: '24px', justifyContent: 'center', flexWrap: 'wrap' }}>
                <CreateNoteButton 
                  size="sm" 
                  color="#6D28D9"
                  iconOnly={true}
                  onClick={() => alert('Create Visual note')}
                />
                <CreateNoteButton 
                  size="md" 
                  color="#3B82F6"
                  iconOnly={true}
                  onClick={() => alert('Create Auditory note')}
                />
                <CreateNoteButton 
                  size="lg" 
                  color="#059669"
                  iconOnly={true}
                  onClick={() => alert('Create Reading note')}
                />
                <CreateNoteButton 
                  size="lg" 
                  color="#F59E0B"
                  iconOnly={true}
                  onClick={() => alert('Create Kinesthetic note')}
                />
              </div>
            </div>
          </div>

          {/* AudioPlayButton Variants */}
          <div style={{ marginBottom: '40px' }}>
            <h2 style={{ color: '#E0E0E0', marginBottom: '20px', fontSize: '24px' }}>AudioPlayButton</h2>
            <div style={{ marginBottom: '24px' }}>
              <h3 style={{ color: '#B0B0B0', marginBottom: '12px', fontSize: '16px' }}>Toggle Play/Pause (Click to test animation)</h3>
              <div style={{ display: 'flex', gap: '32px', justifyContent: 'center', flexWrap: 'wrap', alignItems: 'center' }}>
                <div style={{ textAlign: 'center' }}>
                  <p style={{ color: '#9E9E9E', marginBottom: '12px', fontSize: '12px' }}>Small</p>
                  <AudioPlayButton 
                    size="sm" 
                    color="#6D28D9"
                    isPlaying={isPlaying}
                    onClick={() => setIsPlaying(!isPlaying)}
                  />
                </div>
                <div style={{ textAlign: 'center' }}>
                  <p style={{ color: '#9E9E9E', marginBottom: '12px', fontSize: '12px' }}>Medium</p>
                  <AudioPlayButton 
                    size="md" 
                    color="#3B82F6"
                    isPlaying={isPlaying}
                    onClick={() => setIsPlaying(!isPlaying)}
                  />
                </div>
                <div style={{ textAlign: 'center' }}>
                  <p style={{ color: '#9E9E9E', marginBottom: '12px', fontSize: '12px' }}>Large</p>
                  <AudioPlayButton 
                    size="lg" 
                    color="#059669"
                    isPlaying={isPlaying}
                    onClick={() => setIsPlaying(!isPlaying)}
                  />
                </div>
              </div>
            </div>
            <div>
              <h3 style={{ color: '#B0B0B0', marginBottom: '12px', fontSize: '16px' }}>All VARK Colors</h3>
              <div style={{ display: 'flex', gap: '24px', justifyContent: 'center', flexWrap: 'wrap' }}>
                <div style={{ textAlign: 'center' }}>
                  <p style={{ color: '#9E9E9E', marginBottom: '8px', fontSize: '12px' }}>Visual</p>
                  <AudioPlayButton 
                    size="md" 
                    color="#6D28D9"
                    isPlaying={isPlaying}
                    onClick={() => setIsPlaying(!isPlaying)}
                  />
                </div>
                <div style={{ textAlign: 'center' }}>
                  <p style={{ color: '#9E9E9E', marginBottom: '8px', fontSize: '12px' }}>Auditory</p>
                  <AudioPlayButton 
                    size="md" 
                    color="#3B82F6"
                    isPlaying={isPlaying}
                    onClick={() => setIsPlaying(!isPlaying)}
                  />
                </div>
                <div style={{ textAlign: 'center' }}>
                  <p style={{ color: '#9E9E9E', marginBottom: '8px', fontSize: '12px' }}>Reading</p>
                  <AudioPlayButton 
                    size="md" 
                    color="#059669"
                    isPlaying={isPlaying}
                    onClick={() => setIsPlaying(!isPlaying)}
                  />
                </div>
                <div style={{ textAlign: 'center' }}>
                  <p style={{ color: '#9E9E9E', marginBottom: '8px', fontSize: '12px' }}>Kinesthetic</p>
                  <AudioPlayButton 
                    size="md" 
                    color="#F59E0B"
                    isPlaying={isPlaying}
                    onClick={() => setIsPlaying(!isPlaying)}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Interactive Counter */}
          <div style={{ 
            background: 'rgba(255, 255, 255, 0.05)',
            backdropFilter: 'blur(10px)',
            borderRadius: '16px',
            padding: '32px',
            marginBottom: '40px',
            border: '1px solid rgba(255, 255, 255, 0.1)'
          }}>
            <h2 style={{ color: '#E0E0E0', marginBottom: '20px', fontSize: '24px' }}>Interactive Counter</h2>
            <div style={{ 
              fontSize: '72px', 
              fontWeight: 'bold',
              background: 'linear-gradient(135deg, #F59E0B 0%, #EF4444 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              marginBottom: '24px'
            }}>
              {count}
            </div>
     
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button 
                variant="primary" 
                color="#F59E0B"
                onClick={() => setCount(count + 1)}
              >
                + Increment
              </Button>
              <Button 
                variant="secondary" 
                onClick={() => setCount(count - 1)}
              >
                - Decrement
              </Button>
              <Button 
                variant="outline" 
                color="#059669"
                onClick={() => setCount(0)}
              >
                Reset
              </Button>
            </div>
          </div>

          {/* Size Variants */}
          <div style={{ marginBottom: '40px' }}>
            <h2 style={{ color: '#E0E0E0', marginBottom: '20px', fontSize: '24px' }}>Sizes</h2>
            <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap', alignItems: 'center' }}>
              <Button variant="primary" color="#3B82F6" size="sm">Small</Button>
              <Button variant="primary" color="#3B82F6" size="md">Medium</Button>
              <Button variant="primary" color="#3B82F6" size="lg">Large</Button>
            </div>
          </div>
          

          {/* Buttons Import Features Test */}
          <div style={{ marginBottom: '40px' }}>
            <h2 style={{ color: '#E0E0E0', marginBottom: '20px', fontSize: '24px' }}>Test Buttons Import</h2>
            <div style={{ display: 'flex', width: '100%', flexWrap: 'wrap', columnGap: '12px', rowGap: '24px', alignItems: 'center', justifyContent: 'center', maxWidth: '760px', margin: '0 auto' }}>
              <ImportRecordAudioFeature
                onAudioAction={handleAudioImportTest}
                size="md"
              />
              <ImportFileFeature
                onFileImport={handleFileImportTest}
                size="md"
              />
              <PastLinkVideoFeature
                onYoutubeLink={handleYoutubeLinkTest}
                size="md"
              />
            </div>

            {youtubeProgress && (
              <div style={{
                marginTop: '14px',
                width: '420px',
                background: 'rgba(59,130,246,0.08)',
                border: '1px solid rgba(96,165,250,0.35)',
                borderRadius: '10px',
                padding: '10px 12px',
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: '#DBEAFE', marginBottom: '8px' }}>
                  <span>Progression YouTube</span>
                  <span>{Math.max(0, Math.min(100, Math.round(Number(youtubeProgress.progress || 0))))}%</span>
                </div>
                <div style={{ width: '100%', height: '8px', background: 'rgba(255,255,255,0.15)', borderRadius: '999px', overflow: 'hidden' }}>
                  <div
                    style={{
                      width: `${Math.max(0, Math.min(100, Math.round(Number(youtubeProgress.progress || 0))))}%`,
                      height: '100%',
                      background: youtubeProgress.stage === 'error' ? '#F87171' : '#60A5FA',
                      transition: 'width 0.25s ease',
                    }}
                  />
                </div>
                <div style={{ marginTop: '8px', fontSize: '12px', color: youtubeProgress.stage === 'error' ? '#FCA5A5' : '#BFDBFE' }}>
                  {youtubeProgress.message}
                </div>
              </div>
            )}

            <div style={{ marginTop: '18px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', maxWidth: '420px' }}>
              <div style={{ color: '#CBD5E1', fontSize: '13px' }}>
                Contenu importe ({importedContentItems.length})
              </div>
              {importedContentItems.length > 0 && (
                <Button
                  variant="outline"
                  color="#F87171"
                  size="sm"
                  onClick={() => setImportedContentItems([])}
                >
                  Vider
                </Button>
              )}
            </div>
<ImportButton
  mainText="Import file "
  secondaryText="PDF, DOC, PPT, etc."
  width="320px"
  background="#17181C"
    icon={<FiUpload />}
  iconSize={30}
  iconColor="#FFFFFF"
  iconColorHover="#5EEAD4"
  backgroundHover="#1F232B"
  outlineColor="rgba(255,255,255,0.18)"
  outlineColorHover="#5EEAD4"
  outlineWidth={0.5}
  outlineWidthHover={0.5}
  boxShadow="0 2px 10px rgba(0,0,0,0.22)"
  boxShadowHover="0 7px 20px rgba(94,234,212,0.22)"
/>
            <div
              style={{
                marginTop: '10px',
                width: '420px',
                height: '220px',
                background: 'rgba(255,255,255,0.03)',
                border: '1px solid rgba(255,255,255,0.1)',
                borderRadius: '12px',
                padding: '12px',
                overflowY: 'auto',
                color: '#E2E8F0',
              }}
            >
              {importedContentItems.length === 0 ? (
                <div style={{ color: '#64748B', fontSize: '13px' }}>
                  Extracted content will appear here after confirming the import.
                </div>
              ) : (
                importedContentItems.map((item) => (
                  <div key={item.id} style={{ marginBottom: '12px', paddingBottom: '10px', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                    <div style={{ fontSize: '12px', color: item.sourceType === 'pdf' ? '#10B981' : '#60A5FA', marginBottom: '6px' }}>
                      {item.sourceType.toUpperCase()} - {item.fileName}
                    </div>
                    <div style={{ fontSize: '12px', color: '#CBD5E1', lineHeight: 1.45 }}>
                      <MathContentLayer content={item.text} />
                    </div>
                  </div>
                ))
              )}
            </div>
            
          </div>
          

        </div>
      </header>
      </div>
    </div>
  );
}

export default TestPage;

